import { useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "./Button";

// Cancel + confirm pair at the foot of every Entry edit panel. The two
// EditableRow + CareerGraph RoleEditor sites used to host their own copy
// of this block (state, tooltip, disabled wiring). Same affordance, same
// validation gating, same workshop styling - consolidated here.
//
// `missingFields` is the externally-computed list of required-field
// labels still empty. When non-empty:
//   - confirm is disabled
//   - clicking confirm reveals a tooltip listing the labels
// Caller never sees the tooltip state; it's local and resets on close.
//
// Neither button saves: confirm just signals "I'm done with this panel"
// (close), cancel signals "drop my pending overlay". The page-level
// SaveBar owns commit (ADR-0005).

interface EditPanelButtonsProps {
  onCancel: () => void;
  onConfirm: () => void;
  /**
   * Stage a delete of the row. When omitted, the Löschen button doesn't
   * render - drafts pass undefined since ABBRECHEN removes them anyway.
   * The handler is responsible for closing the panel; the primitive does
   * not call onConfirm/onCancel after delete.
   */
  onDelete?: () => void;
  cancelLabel?: string;
  confirmLabel?: string;
  deleteLabel?: string;
  missingFields?: readonly string[];
}

export function EditPanelButtons({
  onCancel,
  onConfirm,
  onDelete,
  cancelLabel,
  confirmLabel,
  deleteLabel,
  missingFields = [],
}: EditPanelButtonsProps) {
  const intl = useIntl();
  const cancelText = cancelLabel ?? intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" });
  const confirmText = confirmLabel ?? intl.formatMessage({ id: "search.done", defaultMessage: "Done" });
  const deleteText = deleteLabel ?? intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" });
  const [showMissing, setShowMissing] = useState(false);
  const tooltipVisible = showMissing && missingFields.length > 0;

  const handleConfirm = () => {
    if (missingFields.length > 0) {
      setShowMissing(true);
      return;
    }
    onConfirm();
  };

  return (
    <div className="flex items-center justify-between gap-2 pt-1">
      <div>
        {onDelete ? (
          <Button variant="secondary" onClick={onDelete}>
            {deleteText}
          </Button>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={onCancel}>
          {cancelText}
        </Button>
        <div className="relative">
          {tooltipVisible ? (
            <div
              role="tooltip"
              className="absolute bottom-full right-0 mb-2 w-max max-w-[240px] rounded-md bg-page px-3 py-2 text-left text-[11px] leading-snug text-accent shadow-lg ring-1 ring-accent/40"
            >
              {intl.formatMessage({ id: "design.stillFillIn", defaultMessage: "Please still fill in: {fields}" }, { fields: missingFields.join(", ") })}
            </div>
          ) : null}
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={missingFields.length > 0}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}
